import React, { PureComponent, createRef } from "react";
import PropTypes from "prop-types";
import { Row, Col, Input } from "reactstrap";
import LoadingOverlay from "../LoadingOverlay";
import TableRow from "./TableRow";
import TableHeaderCell from "./TableHeaderCell";
import TableHeaderCellSelection from "./TableHeaderCellSelection";
import Pagination from "./Pagination";
import DraggingPreview from "./DraggingPreview";
import DropDivTarget from "./DropDivTarget";
import { debounce } from "lodash";
import ExportReportButton from "@/views/Components/ExportReport/ExportReportButton";
import { css } from "react-emotion";
import "./styles.css";

const tableToolBarStyles = css`
  display: flex;
  flex-wrap: nowrap;
  margin: 10px 0;

  .col {
    flex: 1 1 auto;
    padding: 0 5px;
    width: auto;
  }

  .col:first-child {
    padding-left: 0;
  }

  .col:last-child {
    padding-right: 0;
  }

  .col-export {
    flex-grow: 0;
  }
`;

const tableContainerStyles = css`
  position: relative;
  min-height: 100px;
`;

class DnDTable extends PureComponent {
  state = {
    selectedRows: [],
    isDragging: false,
    filter: "",
  };

  componentDidUpdate({ queryParams: { filter: prevFilter } = {} }) {
    const { queryParams: { filter } = {} } = this.props;
    if (prevFilter !== filter) {
      this.setState({ filter });
    }
  }

  resetSelectedRows = () => this.setState({ selectedRows: [] });

  onChangeSize = size => {
    this.resetSelectedRows();
    this.props.onChangeQueryParams({ size, page: 1 });
  };

  onChangePage = page => {
    this.resetSelectedRows();
    this.props.onChangeQueryParams({ page });
  };

  onChangeFilter = ({ target: { value: filter } }) =>
    this.setState({ filter }, this.delayedFilterChange);

  onChangeSort = ({ dataField, orderBy }) => {
    const sort = `${dataField},${orderBy}`;
    this.props.onChangeQueryParams({ sort });
  };

  onTypeChange = ({ target: { value: type } }) => {
    this.resetSelectedRows();
    this.props.onChangeQueryParams({ type, page: 1 });
  };

  onCheckRow = (id, isChecked) =>
    this.setState(({ selectedRows }) => ({
      selectedRows: isChecked
        ? selectedRows.filter(x => x !== id)
        : selectedRows.concat(id),
    }));

  onClickAll = () => {
    const { data } = this.props;
    this.setState(({ selectedRows }) => ({
      selectedRows: selectedRows.length ? [] : data.map(({ id }) => id),
    }));
  };

  delayedFilterChange = debounce(function() {
    this.resetSelectedRows();
    this.props.onChangeQueryParams({
      filter: this.state.filter,
      page: 1,
    });
  }, 800);

  handleDragRow = id =>
    this.setState(({ selectedRows }) => ({
      isDragging: true,
      selectedRows: selectedRows.includes(id)
        ? selectedRows
        : selectedRows.concat(id),
    }));

  handleEndDrag = () => {
    if (this.state.selectedRows.length === 1) {
      this.setState({ selectedRows: [], isDragging: false });
    } else {
      this.setState({ isDragging: false });
    }
  };

  previewRef = createRef();
  tableRef = createRef();

  handleImagePreviewShow = () => {
    if (this.previewRef.current) {
      this.previewRef.current.style.display = "block";
      this.previewRef.current.style.width =
        this.tableRef.current.offsetWidth + "px";
    }
  };

  handleImagePreviewMove = event => {
    if (this.previewRef.current) {
      this.previewRef.current.style.transform = `translate(${event.clientX -
        20}px, ${event.clientY - 20}px)`;
    }
  };

  handleImagePreviewHide = () => {
    if (this.previewRef.current) {
      this.previewRef.current.style.display = "none";
    }
  };

  render() {
    const { selectedRows, isDragging, filter } = this.state;
    const {
      queryParams: { page: queryPage, size: querySize, sort, type } = {},
      isLoading,
      data = [],
      totalEntities,
      sourceType,
      columns = [],
      acceptsType,
      onDrop,
      selectable,
      showAccountTypes,
    } = this.props;

    const [sortedField, orderBy] = sort ? sort.split(",") : [];

    const page = Number(queryPage) || 1;
    const size = Number(querySize) || 10;

    return (
      <div>
        <LoadingOverlay loading={isLoading} />
        <Row className={tableToolBarStyles}>
          {this.props.requestReport && (
            <Col className="col-export">
              <ExportReportButton
                params={{
                  page,
                  size,
                  filter,
                  sort,
                  type,
                  branch: sourceType,
                }}
                requestReport={this.props.requestReport}
                notext
              />
            </Col>
          )}

          {showAccountTypes && (
            <Col>
              <Input
                type="select"
                name="select"
                id="accountType"
                value={type}
                onChange={this.onTypeChange}>
                <option value="ALL">All account types</option>
                <option value="MOBILE">Only mobile</option>
                <option value="DESKTOP">Only desktop</option>
              </Input>
            </Col>
          )}
          <Col>
            <Input
              type="text"
              name="text"
              placeholder="Search"
              value={filter}
              onChange={this.onChangeFilter}
            />
          </Col>
        </Row>
        <div className={tableContainerStyles}>
          <DropDivTarget acceptsType={acceptsType} onDrop={onDrop} />

          <table
            className="table table-hover table-bordered table-sm dnd-table"
            ref={this.tableRef}>
            <thead>
              <tr className="dnd-table-header">
                {selectable && (
                  <TableHeaderCellSelection
                    onClickHeaderSelect={this.onClickAll}
                    selected={selectedRows}
                    checked={
                      selectedRows.length >= data.length && data.length !== 0
                    }
                    isIndeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < data.length
                    }
                  />
                )}
                {columns.map(column => (
                  <TableHeaderCell
                    key={column.title}
                    title={column.title}
                    dataAlign={column.dataAlign}
                    dataSort={column.dataSort}
                    sortedField={sortedField}
                    orderBy={orderBy}
                    dataField={column.dataField}
                    onChangeSort={this.onChangeSort}
                    style={column.style}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <TableRow
                  key={row.id + row.accountId}
                  type={sourceType}
                  row={row}
                  columns={columns}
                  isChecked={selectedRows.includes(row.id)}
                  onCheck={this.onCheckRow}
                  onBeginDrag={this.handleDragRow}
                  onEndDrag={this.handleEndDrag}
                  isDragging={isDragging}
                  handleImagePreviewShow={this.handleImagePreviewShow}
                  handleImagePreviewMove={this.handleImagePreviewMove}
                  handleImagePreviewHide={this.handleImagePreviewHide}
                  selectable={selectable}
                />
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          size={size}
          page={page}
          onChangeSize={this.onChangeSize}
          onChangePage={this.onChangePage}
          total={totalEntities}
        />

        <DraggingPreview
          forwardedRef={this.previewRef}
          data={data}
          columns={columns}
          selectedRows={selectedRows}
          selectable={selectable}
        />
      </div>
    );
  }
}

DnDTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  totalEntities: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  sourceType: PropTypes.string.isRequired,
  acceptsType: PropTypes.string.isRequired,
  onDrop: PropTypes.func.isRequired,
  queryParams: PropTypes.object.isRequired,
  onChangeQueryParams: PropTypes.func.isRequired,
  requestReport: PropTypes.func,
  selectable: PropTypes.bool,
  showAccountTypes: PropTypes.bool,
};

DnDTable.defaultProps = {
  isLoading: false,
  totalEntities: 0,
  selectable: true,
  showAccountTypes: true,
};

export default DnDTable;
